import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { StyledH2 } from "@aureskonnect/react-ui";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import styled from "styled-components";

import { capitalize } from "../../../helpers/general";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";

const StyledModal = styled(Modal)`
    max-width: 670px;

    .modal-content {
        border-radius: 12px;
    }

    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        h2 {
            font-size: 1.5rem;
            font-weight: 600;
        }

        .close,
        button[aria-label="Close"] {
            margin: 0;
            padding: 0;
        }
    }
`;

const MessageContainer = styled.div`
    padding: 8px 16px;
`;

const MessageHeader = styled.p`
    font-size: 0.95rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e9ecef;
`;

const MessageList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 250px;
    overflow-y: auto;
    padding-right: 8px;

    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: #a1a1a1;
    }
`;

interface ColorProps {
    $mainColor: string;
}

const MessageItem = styled.li<ColorProps>`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    background: #f8f9fa;
    border-radius: 8px;
    border-left: 3px solid ${({ $mainColor }) => $mainColor};
    font-size: 0.9rem;
    color: #495057;
    transition: all 0.2s ease;

    &:hover {
        background: #f1f3f4;
        transform: translateX(2px);
    }
`;

const ItemNumber = styled.span<ColorProps>`
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    background: ${({ $mainColor }) => $mainColor};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    color: #fff;
    font-weight: 600;
`;

const ItemText = styled.span`
    flex: 1;
    line-height: 1.5;
`;

const SimpleMessage = styled.p`
    font-size: 0.95rem;
    color: #495057;
    margin: 0;
    line-height: 1.6;
    white-space: pre-line;
`;

type ModalPublishType = {
    setIsModalOpened: (value: boolean) => void;
    isModalOpened: boolean;
    msg: string;
};

export default function ModalErrorInfo({
    setIsModalOpened,
    isModalOpened,
    msg,
}: ModalPublishType) {
    const { t } = useTranslation();
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );

    const closeModal = () => setIsModalOpened(false);

    const content = typeof msg === "string" ? msg : String(msg ?? "");
    const lines = content.split("\n").filter((line) => line.trim());

    const hasMultipleLines = lines.length > 1;
    const header = hasMultipleLines ? lines[0] : null;
    const items = hasMultipleLines ? lines.slice(1) : [];

    return (
        <StyledModal centered isOpen={isModalOpened}>
            <ModalHeader toggle={closeModal}>
                <StyledH2>{capitalize(t("Attention"))}</StyledH2>
            </ModalHeader>

            <ModalBody>
                <MessageContainer>
                    {hasMultipleLines ? (
                        <>
                            {header && <MessageHeader>{header}</MessageHeader>}
                            <MessageList>
                                {items.map((item, idx) => {
                                    const isSubItem = item.trimStart().startsWith("→");
                                    const displayText = item
                                        .replace(/^[•\\*]\s*/, "")
                                        .trim();
                                    return (
                                        <MessageItem
                                            key={idx}
                                            $mainColor={mainColor}
                                            style={
                                                isSubItem
                                                    ? {
                                                          marginLeft: "28px",
                                                          borderLeftColor: "#dee2e6",
                                                          background: "#fff",
                                                          fontSize: "0.85rem",
                                                      }
                                                    : undefined
                                            }
                                        >
                                            {!isSubItem && (
                                                <ItemNumber
                                                    $mainColor={mainColor}
                                                >
                                                    {items
                                                        .slice(0, idx + 1)
                                                        .filter(
                                                            (l) =>
                                                                !l
                                                                    .trimStart()
                                                                    .startsWith(
                                                                        "→"
                                                                    )
                                                        ).length}
                                                </ItemNumber>
                                            )}
                                            <ItemText>{displayText}</ItemText>
                                        </MessageItem>
                                    );
                                })}
                            </MessageList>
                        </>
                    ) : (
                        <SimpleMessage>{content}</SimpleMessage>
                    )}
                </MessageContainer>
            </ModalBody>

            <ModalFooter style={{ borderTop: "1px solid #e9ecef" }}>
                <CustomSecondaryColorButton
                    outline
                    variant="primary"
                    onClick={closeModal}
                    rounded
                >
                    {t("Close")}
                </CustomSecondaryColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
